import { useEffect, useMemo, useState } from "react";
import { useSeo } from "../lib/seo";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, FileCode2, Search, Store } from "lucide-react";
import { SystemLogo } from "../components/connector/SystemLogo";
import { publicCatalog, CATEGORY_LABELS } from "../lib/api";
import type { PublicListing } from "../lib/api";
import { cn } from "../lib/cn";

/** The public connector catalog — one page, browsable by category.
 *
 * Reads the live official catalog (no auth) so what a visitor sees is exactly
 * what a workspace gets on day one.
 */
export default function PublicExchange() {
  useSeo({
    title: 'Connector Exchange — 100+ ready-made integrations',
    description: 'Browse 100+ ready-made connectors on the Wise Exchange — Salesforce, SAP, Slack, Jira, Stripe and more. Install, add your credentials, and integrate in minutes.',
    path: '/exchange',
  });
  const [listings, setListings] = useState<PublicListing[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>("");
  const [q, setQ] = useState("");

  useEffect(() => {
    publicCatalog(300)
      .then(setListings)
      .catch(() => setError("Couldn't load the catalog."));
  }, []);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const l of listings ?? []) {
      if (l.category) counts.set(l.category, (counts.get(l.category) ?? 0) + 1);
    }
    return Object.keys(CATEGORY_LABELS)
      .filter((k) => counts.has(k))
      .map((k) => ({ key: k, label: CATEGORY_LABELS[k], count: counts.get(k)! }));
  }, [listings]);

  const shown = useMemo(() => {
    let rows = listings ?? [];
    if (category) rows = rows.filter((l) => l.category === category);
    const term = q.trim().toLowerCase();
    if (term) {
      rows = rows.filter(
        (l) =>
          l.display_name.toLowerCase().includes(term) ||
          l.system_type.toLowerCase().includes(term) ||
          l.description.toLowerCase().includes(term),
      );
    }
    return rows;
  }, [listings, category, q]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-white to-slate-50">
        <div className="mx-auto max-w-6xl px-6 py-16 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700 ring-1 ring-inset ring-brand-100">
            <Store className="h-3.5 w-3.5" /> Wise Exchange
          </span>
          <h1 className="mt-5 text-4xl font-bold tracking-tight text-slate-900">
            {listings ? `${listings.length} connectors` : "The connector catalog"}, ready to install
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
            Connectors we build, test and maintain. Install one, add your credentials, and you're
            integrated — no code, no waiting.
          </p>
          <div className="relative mx-auto mt-8 max-w-md">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search connectors…"
              className="focus-ring w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm shadow-soft"
            />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-6 py-10">
        {/* Category rail */}
        {categories.length > 1 && (
          <div className="mb-8 flex flex-wrap items-center gap-2">
            <button
              onClick={() => setCategory("")}
              className={cn(
                "focus-ring rounded-lg px-3 py-1.5 text-sm font-medium transition",
                category === ""
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-600 ring-1 ring-slate-200 hover:text-slate-900",
              )}
            >
              All <span className={category === "" ? "text-white/60" : "text-slate-400"}>{listings?.length ?? 0}</span>
            </button>
            {categories.map((c) => (
              <button
                key={c.key}
                onClick={() => setCategory(category === c.key ? "" : c.key)}
                className={cn(
                  "focus-ring rounded-lg px-3 py-1.5 text-sm font-medium transition",
                  category === c.key
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-600 ring-1 ring-slate-200 hover:text-slate-900",
                )}
              >
                {c.label}{" "}
                <span className={category === c.key ? "text-white/60" : "text-slate-400"}>{c.count}</span>
              </button>
            ))}
          </div>
        )}

        {error ? (
          <p className="py-20 text-center text-slate-500">{error}</p>
        ) : !listings ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="h-40 animate-pulse rounded-2xl bg-white ring-1 ring-slate-100" />
            ))}
          </div>
        ) : shown.length === 0 ? (
          <p className="py-20 text-center text-slate-500">No connectors match your search.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((l) => (
              <div
                key={l.slug}
                className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-card transition-all hover:-translate-y-1 hover:shadow-lift"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-50 ring-1 ring-slate-200">
                    <SystemLogo
                      iconSlug={l.icon_slug}
                      systemType={l.system_type}
                      brandColor={l.brand_color}
                      name={l.display_name}
                      size={26}
                    />
                  </span>
                  <BadgeCheck className="h-4 w-4 text-brand-500" aria-label="Maintained by Wise 2.0" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-slate-900">{l.display_name}</h3>
                <p className="mt-1.5 line-clamp-3 flex-1 text-sm leading-relaxed text-slate-500">
                  {l.description}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-slate-100 pt-4 text-xs text-slate-400">
                  {l.capability_count > 0 && (
                    <span className="font-medium text-slate-500">
                      {l.capability_count} operation{l.capability_count === 1 ? "" : "s"}
                    </span>
                  )}
                  <span>{l.param_count} settings</span>
                  {l.has_code && (
                    <span className="inline-flex items-center gap-1">
                      <FileCode2 className="h-3 w-3" /> tested
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-14 flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <p className="text-lg font-semibold text-slate-900">Don't see what you need?</p>
          <p className="max-w-xl text-sm text-slate-500">
            Describe any system in a sentence and Wise 2.0 builds the connector for you — usually in
            under two minutes.
          </p>
          <Link
            to="/signup"
            className="focus-ring mt-2 inline-flex h-11 items-center gap-2 rounded-xl bg-brand-gradient px-6 text-sm font-semibold text-white shadow-lift transition hover:-translate-y-0.5"
          >
            Get started free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
